import React, { useMemo, useState } from 'react';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/**
 * A compact, brutalist month calendar with single-date OR date-range selection.
 * First tap sets the start (that day, from 12 AM); the next tap sets the end (that
 * day, through 11:59 PM). Tapping again starts a fresh selection.
 *
 * Props:
 *   selected   — array of yyyy-MM-dd strings currently chosen (all days in range)
 *   onPickRange(startIso, endIso) — called on each tap with the current selection
 *   minIso     — earliest selectable day (past days are disabled)
 */
export default function DateRangeCalendar({ selected = [], onPickRange, minIso }) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const [view, setView] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [anchor, setAnchor] = useState(null); // first tap awaiting its pair

  const { year, month } = view;
  const monthName = new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const startOffset = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const goMonth = (delta) => setView(({ year: y, month: m }) => {
    const nm = m + delta;
    return { year: y + Math.floor(nm / 12), month: ((nm % 12) + 12) % 12 };
  });

  const click = (d) => {
    const id = iso(year, month, d);
    if (minIso && id < minIso) return;
    if (!anchor) {
      setAnchor(id);
      onPickRange?.(id, id);
    } else {
      const [s, e] = anchor <= id ? [anchor, id] : [id, anchor];
      onPickRange?.(s, e);
      setAnchor(null);
    }
  };

  const btn = 'flex h-8 w-8 items-center justify-center font-mono text-[7px] font-bold uppercase';

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <button type="button" onClick={() => goMonth(-1)} aria-label="Previous month" className="flex h-7 w-7 items-center justify-center border-2 border-black bg-white hover:bg-gray-100">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <span className="font-mono text-[11px] font-bold uppercase tracking-wide">{monthName}</span>
        <button type="button" onClick={() => goMonth(1)} aria-label="Next month" className="flex h-7 w-7 items-center justify-center border-2 border-black bg-white hover:bg-gray-100">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M9 18l6-6-6-6" /></svg>
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {WEEKDAYS.map((w, i) => (
          <div key={i} className="flex h-5 items-center justify-center font-mono text-[9px] font-bold text-gray-400">{w}</div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const id = iso(year, month, d);
          const on = selectedSet.has(id);
          const disabled = !!minIso && id < minIso;
          return (
            <button
              key={id}
              type="button"
              disabled={disabled}
              onClick={() => click(d)}
              aria-pressed={on}
              className={`flex h-8 w-full items-center justify-center border font-mono text-[12px] transition-colors ${
                disabled ? 'cursor-not-allowed border-transparent text-gray-300'
                  : on ? 'border-black bg-black font-bold text-white'
                  : 'border-transparent text-black hover:border-black hover:bg-gray-100'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}
